import React, { useEffect, useRef } from 'react';
import { displayToEditorHtml, editorHtmlToDisplay } from '../utils/htmlMarkers';

const PROTECTED_SELECTOR = '.html-marker-chip, .html-entity-marker';

/** Ordered signature of protected markers (chips + entity spans). */
function markerSignature(el) {
  const chips = Array.from(el.querySelectorAll('.html-marker-chip'))
    .map((chip) => `T${chip.getAttribute('data-marker-id')}${chip.getAttribute('data-marker-side')}`);
  const entities = Array.from(el.querySelectorAll('.html-entity-marker'))
    .map((node) => `E${node.getAttribute('data-entity-id')}`);
  return `${chips.join(',')}|${entities.join(',')}`;
}

function isProtectedMarker(node) {
  return !!(node && node.nodeType === Node.ELEMENT_NODE && (
    node.classList.contains('html-marker-chip')
    || node.classList.contains('html-entity-marker')
  ));
}

function rangeTouchesProtectedMarker(range) {
  if (!range || range.collapsed) return false;
  const fragment = range.cloneContents();
  return fragment.querySelector(PROTECTED_SELECTOR) !== null;
}

/** Node immediately before/after a collapsed caret (skipping empty text nodes). */
function adjacentNode(range, backward) {
  const { startContainer: node, startOffset: offset } = range;

  if (node.nodeType === Node.TEXT_NODE) {
    const atEdge = backward ? offset === 0 : offset === node.textContent.length;
    if (!atEdge) return null;
  } else {
    const child = node.childNodes[backward ? offset - 1 : offset];
    if (child) return child;
    return null;
  }

  let sibling = backward ? node.previousSibling : node.nextSibling;
  while (sibling && sibling.nodeType === Node.TEXT_NODE && sibling.textContent === '') {
    sibling = backward ? sibling.previousSibling : sibling.nextSibling;
  }
  return sibling;
}

function placeCaretAtEnd(el) {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Plain-text editable cell that renders HTML tags as numbered marker chips.
 *
 * This is intentionally NOT a rich-text editor: there is no toolbar, pasted
 * content is inserted as plain text, and Enter inserts a newline.
 *
 * Chips and entity markers are read-only. Users can edit the text around them
 * but cannot delete, cut, drag, overwrite, or otherwise change a chip or
 * preserved entity marker.
 */
function MarkerEditableCell({
  displayWithMarkers,
  tagMap,
  entityMap,
  onChange,
  modified = false,
  disabled = false,
  ariaLabel,
  lastEditHighlight = null,
}) {
  const ref = useRef(null);
  const lastEmitted = useRef(null);
  const lockedSignature = useRef(null);
  const lastValidHtml = useRef('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (lastEmitted.current === displayWithMarkers) return;
    const html = displayToEditorHtml(
      displayWithMarkers || '',
      tagMap || {},
      entityMap || {},
      lastEditHighlight,
    );
    if (el.innerHTML !== html) {
      el.innerHTML = html;
    }
    lastEmitted.current = displayWithMarkers;
    lockedSignature.current = markerSignature(el);
    lastValidHtml.current = el.innerHTML;
  }, [displayWithMarkers, tagMap, entityMap, lastEditHighlight]);

  const emitChange = () => {
    const el = ref.current;
    if (!el || disabled) return;

    if (markerSignature(el) !== lockedSignature.current) {
      el.innerHTML = lastValidHtml.current;
      placeCaretAtEnd(el);
      return;
    }

    lastValidHtml.current = el.innerHTML;
    const next = editorHtmlToDisplay(el.innerHTML, { ...(tagMap || {}) }, { ...(entityMap || {}) });
    lastEmitted.current = next.displayWithMarkers;
    onChange(next.displayWithMarkers, next.tagMap, next.entityMap);
  };

  const currentRange = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    return ref.current && ref.current.contains(range.commonAncestorContainer) ? range : null;
  };

  const handleBeforeInput = (event) => {
    const type = event.inputType || '';
    const range = currentRange();

    if (range && rangeTouchesProtectedMarker(range)) {
      event.preventDefault();
      return;
    }

    if (type === 'insertFromDrop' || type === 'deleteByDrag') {
      event.preventDefault();
      return;
    }

    if (range && range.collapsed) {
      if (type.startsWith('deleteContentBackward') && isProtectedMarker(adjacentNode(range, true))) {
        event.preventDefault();
      } else if (type.startsWith('deleteContentForward') && isProtectedMarker(adjacentNode(range, false))) {
        event.preventDefault();
      } else if (type.startsWith('deleteWord') || type.startsWith('deleteSoft') || type.startsWith('deleteHard')) {
        const backward = type.endsWith('Backward');
        if (isProtectedMarker(adjacentNode(range, backward))) event.preventDefault();
      }
    }
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      document.execCommand('insertText', false, '\n');
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      const range = currentRange();
      if (!range) return;
      if (rangeTouchesProtectedMarker(range)) {
        event.preventDefault();
        return;
      }
      if (range.collapsed && isProtectedMarker(adjacentNode(range, event.key === 'Backspace'))) {
        event.preventDefault();
      }
    }
  };

  const handleCut = (event) => {
    const range = currentRange();
    if (range && rangeTouchesProtectedMarker(range)) {
      event.preventDefault();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const range = currentRange();
    if (range && rangeTouchesProtectedMarker(range)) return;
    const text = event.clipboardData?.getData('text/plain') || '';
    document.execCommand('insertText', false, text);
  };

  const blockDrag = (event) => event.preventDefault();

  return (
    <div
      ref={ref}
      className={`marker-edit-cell${modified ? ' marker-edit-cell--modified' : ''}${disabled ? ' marker-edit-cell--disabled' : ''}`}
      contentEditable={!disabled}
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={ariaLabel}
      onBeforeInput={handleBeforeInput}
      onInput={emitChange}
      onKeyDown={handleKeyDown}
      onCut={handleCut}
      onPaste={handlePaste}
      onDrop={blockDrag}
      onDragStart={blockDrag}
    />
  );
}

export default MarkerEditableCell;
